/**
 * Bulk Re-engagement (Dormant User Reach-Out) modal.
 *
 * Admin-only. Lets you preview how many dormant users would receive the
 * "We miss you" email and either dry-run or actually send.
 *
 * Audience is defined by `dormant_days` (users whose `last_seen_at` is
 * older than that, OR who never had `last_seen_at` set). Idempotent per
 * `campaign` so accidental double-clicks won't double-email.
 */
import React, { useState, useCallback } from 'react';
import { Megaphone, RefreshCw, Send } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { apiFetch } from '@/hooks/useFetch';

export function ReengagementModal({ open, onOpenChange, onSent }) {
  const [dormantDays, setDormantDays] = useState(30);
  const [campaign, setCampaign] = useState(`recovery-${new Date().toISOString().slice(0, 10)}`);
  const [dryRun, setDryRun] = useState(true);
  const [cap, setCap] = useState(500);
  const [preview, setPreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const reset = useCallback(() => {
    setPreview(null);
    setIsPreviewing(false);
    setIsSending(false);
  }, []);

  const handlePreview = async () => {
    setIsPreviewing(true);
    setPreview(null);
    const { data, error } = await apiFetch(
      `/api/admin/users/dormant-preview?dormant_days=${dormantDays}&cap=${cap}`,
      { method: 'GET' },
    );
    setIsPreviewing(false);
    if (error) {
      toast.error(error || 'Failed to preview audience');
      return;
    }
    setPreview(data);
  };

  const handleSend = async () => {
    if (dormantDays < 0) {
      toast.error('Dormant days must be >= 0');
      return;
    }
    setIsSending(true);
    const { data, error } = await apiFetch('/api/admin/users/bulk-reach-out', {
      method: 'POST',
      body: {
        dormant_days: Number(dormantDays),
        campaign,
        dry_run: dryRun,
        cap: Number(cap),
      },
    });
    setIsSending(false);
    if (error) {
      toast.error(error || 'Bulk reach-out failed');
      return;
    }
    const verb = dryRun ? 'Dry-run complete' : 'Re-engagement emails sent';
    toast.success(
      `${verb}: ${data.sent} sent · ${data.skipped_already_sent || 0} already done · ${data.failed || 0} failed`,
    );
    onSent?.(data);
    if (!dryRun) {
      onOpenChange(false);
      reset();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="flex flex-col h-[90vh] sm:h-auto sm:max-h-[90vh] p-0 gap-0 overflow-hidden" data-testid="reengagement-modal">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-[#00E5CC]" />
            Bulk Re-engagement Email
          </DialogTitle>
          <DialogDescription>
            Send a friendly &quot;we miss you&quot; email to dormant users. Idempotent per campaign — anyone already emailed in this campaign is automatically skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="dormant-days">Dormant for at least (days)</Label>
              <Input
                id="dormant-days"
                type="number"
                min={0}
                value={dormantDays}
                onChange={(e) => { setDormantDays(e.target.value); setPreview(null); }}
                data-testid="reengagement-dormant-days-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="reengagement-cap">Cap (max recipients)</Label>
              <Input
                id="reengagement-cap"
                type="number"
                min={1}
                max={2000}
                value={cap}
                onChange={(e) => setCap(e.target.value)}
                data-testid="reengagement-cap-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="reengagement-campaign">Campaign label</Label>
            <Input
              id="reengagement-campaign"
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              placeholder="recovery-YYYY-MM-DD"
              data-testid="reengagement-campaign-input"
            />
            <p className="text-xs text-muted-foreground">
              Re-using a label means anyone already pinged for it is skipped.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="reengagement-dry-run"
              checked={dryRun}
              onCheckedChange={(v) => setDryRun(!!v)}
              data-testid="reengagement-dry-run-checkbox"
            />
            <Label htmlFor="reengagement-dry-run">
              Dry run (counts only — does not send any emails)
            </Label>
          </div>

          <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Audience preview</p>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePreview}
                disabled={isPreviewing}
                data-testid="reengagement-preview-btn"
              >
                {isPreviewing ? (
                  <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Counting…</>
                ) : (
                  'Preview audience'
                )}
              </Button>
            </div>
            {preview ? (
              <div className="text-sm space-y-1">
                <p>
                  <strong>{preview.total_eligible.toLocaleString()}</strong> users are dormant {preview.dormant_days}+ days.
                </p>
                <p className="text-muted-foreground">
                  Will email up to <strong>{preview.will_send_to_at_most.toLocaleString()}</strong> per send (cap {preview.cap}).
                </p>
                {preview.sample?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                      Show {preview.sample.length} sample recipients
                    </summary>
                    <ul className="mt-2 text-xs space-y-0.5 max-h-32 overflow-y-auto">
                      {preview.sample.map((u) => (
                        <li key={u.user_id} className="font-mono text-muted-foreground truncate">
                          {u.email}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Click &quot;Preview audience&quot; to see how many users match before sending.
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t bg-background shrink-0">
          <Button
            variant="outline"
            onClick={() => { onOpenChange(false); reset(); }}
            disabled={isSending}
            data-testid="reengagement-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending || !campaign}
            className={dryRun ? '' : 'bg-[#00E5CC] hover:bg-[#00C9B3] text-[#0d1f2d]'}
            data-testid="reengagement-send-btn"
          >
            {isSending ? (
              <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Working…</>
            ) : (
              <><Send className="h-4 w-4 mr-2" /> {dryRun ? 'Run dry-run' : 'Send emails now'}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ReengagementModal;
